import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Link, useHistory } from "react-router-dom";
import { Dropdown, DropdownToggle, DropdownMenu } from "reactstrap";
import SimpleBar from "simplebar-react";
import { withTranslation } from "react-i18next";

function formatRelativeTime(dateInput) {
  if (!dateInput) return "";
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return "";
  const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diffSec < 60) return "Just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const NotificationDropdown = (props) => {
  const history = useHistory();
  const [menu, setMenu] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const token = localStorage.getItem("token");
  const apiBase = process.env.REACT_APP_DEFAULTPATH || "http://localhost:1337/";

  // Fetch alerts from backend
  const fetchAlerts = useCallback(async () => {
    const currentToken = localStorage.getItem("token");
    if (!currentToken) return;

    try {
      const response = await fetch(`${apiBase}alerts?limit=10`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) return;

      const data = await response.json();
      if (data && data.status) {
        setAlerts(data.items || []);
        setUnreadCount(Number(data.unreadCount) || 0);
      }
    } catch {
      // Poll errors must fail silently (no console spam, no crash)
    }
  }, [apiBase]);

  // Polling every 60 seconds (only if token exists)
  useEffect(() => {
    if (!token) return;

    fetchAlerts();
    const interval = setInterval(fetchAlerts, 60000);
    return () => clearInterval(interval);
  }, [token, fetchAlerts]);

  // If user is not authenticated, do not show bell or poll
  if (!token) {
    return null;
  }

  // Handle clicking a single alert
  const handleAlertClick = (alert) => {
    setMenu(false);

    if (!alert.isRead) {
      // Optimistic update
      setAlerts((prev) =>
        prev.map((a) => (a._id === alert._id ? { ...a, isRead: true } : a))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));

      const currentToken = localStorage.getItem("token");
      if (currentToken) {
        fetch(`${apiBase}alerts/${alert._id}/read`, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${currentToken}`,
            "Content-Type": "application/json",
          },
        }).catch(() => {});
      }
    }

    history.push(alert.type === "asset_change" ? "/dashboard" : "/findings");
  };

  // Mark all unread alerts as read
  const handleMarkAllRead = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Optimistic update
    setAlerts((prev) => prev.map((a) => ({ ...a, isRead: true })));
    setUnreadCount(0);

    const currentToken = localStorage.getItem("token");
    if (currentToken) {
      fetch(`${apiBase}alerts/read-all`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${currentToken}`,
          "Content-Type": "application/json",
        },
      }).catch(() => {});
    }
  };

  return (
    <Dropdown
      isOpen={menu}
      toggle={() => setMenu(!menu)}
      className="dropdown d-inline-block me-2"
      tag="div"
    >
      <DropdownToggle
        className="btn header-item noti-icon position-relative"
        tag="button"
        id="page-header-notifications-dropdown"
        title="Alerts"
        aria-label="Alerts"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          border: "none",
          background: "transparent",
          cursor: "pointer",
          padding: "0 10px",
          height: "100%",
        }}
      >
        <svg
          width="19"
          height="19"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>

        {unreadCount > 0 && (
          <span
            className="badge rounded-pill"
            style={{
              position: "absolute",
              top: "14px",
              right: "4px",
              backgroundColor: "#dc2626",
              color: "#ffffff",
              fontSize: "10px",
              fontWeight: 700,
              padding: "2px 5px",
              lineHeight: 1,
            }}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </DropdownToggle>

      <DropdownMenu
        className="dropdown-menu-lg dropdown-menu-end p-0"
        style={{
          minWidth: "340px",
          maxWidth: "380px",
          boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.25), 0 8px 10px -6px rgba(0, 0, 0, 0.2)",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <div className="p-3 border-bottom d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2">
            <h6 className="m-0 font-weight-bold" style={{ fontSize: "14px" }}>
              {props.t ? props.t("Alerts") : "Alerts"}
            </h6>
            {unreadCount > 0 && (
              <span
                className="badge rounded-pill ms-2"
                style={{
                  backgroundColor: "rgba(220, 38, 38, 0.12)",
                  color: "#dc2626",
                  fontSize: "11px",
                  padding: "3px 7px",
                  fontWeight: 600,
                }}
              >
                {unreadCount} new
              </span>
            )}
          </div>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={handleMarkAllRead}
              className="btn btn-link p-0 text-decoration-none"
              style={{ fontSize: "12px", color: "var(--brand-primary, #2f6fed)" }}
            >
              Mark all read
            </button>
          )}
        </div>

        <SimpleBar style={{ maxHeight: "330px" }}>
          {alerts.length === 0 ? (
            <div className="text-center p-4" style={{ color: "var(--fg-tertiary, #94a3b8)" }}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                className="mb-2"
                style={{ opacity: 0.5 }}
              >
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              <div style={{ fontSize: "13px", fontWeight: 600 }}>No alerts yet</div>
              <div style={{ fontSize: "11px", marginTop: "2px" }}>
                Critical & high severity alerts will appear here
              </div>
            </div>
          ) : (
            alerts.map((alert) => {
              const isCrit = String(alert.severity).toLowerCase() === "critical";
              const dotColor = isCrit ? "#dc2626" : "#ea580c";
              const isSlaAlert = alert.type === "sla_breach" || alert.type === "risk_acceptance_expired";
              const isChangeAlert = alert.type === "asset_change";
              return (
                <div
                  key={alert._id}
                  onClick={() => handleAlertClick(alert)}
                  className="dropdown-item p-3 border-bottom text-wrap"
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                    backgroundColor: !alert.isRead ? "rgba(47, 111, 237, 0.05)" : "transparent",
                    transition: "background-color 0.15s ease",
                  }}
                >
                  {isChangeAlert ? (
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={dotColor}
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ marginTop: "2px", flexShrink: 0 }}
                      role="img"
                      aria-label="Attack surface change"
                    >
                      <title>Attack surface change</title>
                      <path d="M21 12a9 9 0 0 1-15.5 6.2L3 16" />
                      <path d="M3 21v-5h5" />
                      <path d="M3 12a9 9 0 0 1 15.5-6.2L21 8" />
                      <path d="M21 3v5h-5" />
                    </svg>
                  ) : isSlaAlert ? (
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={dotColor}
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ marginTop: "2px", flexShrink: 0 }}
                      role="img"
                      aria-label={alert.type === "sla_breach" ? "SLA breach" : "Risk acceptance expired"}
                    >
                      <title>{alert.type === "sla_breach" ? "SLA breach" : "Risk acceptance expired"}</title>
                      <circle cx="12" cy="12" r="9" />
                      <path d="M12 7v5l3 2" />
                    </svg>
                  ) : (
                    <span
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        backgroundColor: dotColor,
                        marginTop: "5px",
                        flexShrink: 0,
                      }}
                      title={`${alert.severity} severity`}
                    />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: "12.5px",
                        fontWeight: alert.isRead ? 500 : 700,
                        lineHeight: 1.35,
                        color: "inherit",
                        wordBreak: "break-word",
                      }}
                    >
                      {alert.title}
                    </div>
                    {alert.message && (
                      <div
                        style={{
                          fontSize: "11px",
                          opacity: 0.75,
                          marginTop: "2px",
                          lineHeight: 1.3,
                          wordBreak: "break-word",
                        }}
                      >
                        {alert.message}
                      </div>
                    )}
                    <div style={{ fontSize: "10px", opacity: 0.55, marginTop: "4px" }}>
                      {formatRelativeTime(alert.createdAt)}
                    </div>
                  </div>
                  {!alert.isRead && (
                    <span
                      style={{
                        width: "6px",
                        height: "6px",
                        borderRadius: "50%",
                        backgroundColor: "#2f6fed",
                        marginTop: "6px",
                        flexShrink: 0,
                      }}
                      title="Unread"
                    />
                  )}
                </div>
              );
            })
          )}
        </SimpleBar>

        <div className="p-2 border-top text-center">
          <Link
            to="/findings"
            onClick={() => setMenu(false)}
            className="btn btn-sm btn-link p-0 text-decoration-none"
            style={{ fontSize: "12px", color: "var(--brand-primary, #2f6fed)", fontWeight: 600 }}
          >
            View all in Findings →
          </Link>
        </div>
      </DropdownMenu>
    </Dropdown>
  );
};

NotificationDropdown.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(NotificationDropdown);